import React, { PropsWithChildren } from 'react';
import { useToggleConfigOption } from 'widgets/store/configuration/hooks';
import { BooleanKeys } from 'utils/types/BooleanKeys';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import classNames from 'classnames';
import './WidgetLegend.scss';
import { UseToggleConfigOptionRet } from 'widgets/store/configuration/configSlice';
import { WidgetLegendVisibilitySettings } from './WidgetLegendVisibilitySettings';

export type WidgetLegendProps<TConfig> = PropsWithChildren<{
    alignDropdown?: 'left' | 'center' | 'right';
    showSeries?: boolean;
    hideLegendPropertyName?: BooleanKeys<TConfig>;
    toggleLegendVisibleConfigOption?: UseToggleConfigOptionRet;
    toggleShowSeries?: () => void;
}>;

export function WidgetLegend<TConfig>(props: WidgetLegendProps<TConfig>) {
    const {
        alignDropdown,
        showSeries,
        hideLegendPropertyName,
        children,
        toggleLegendVisibleConfigOption: toggleLegendVisibleConfigOptionProps,
        toggleShowSeries,
    } = props;

    const isLocked = useSelectLocked();
    const allowLegendVisibilityEditing = !isLocked;

    const defaultToggleLegendVisibleConfigOption =
        useToggleConfigOption<TConfig>(hideLegendPropertyName);

    const toggleLegendVisibleConfigOption =
        toggleLegendVisibleConfigOptionProps ??
        defaultToggleLegendVisibleConfigOption;

    const { value: legendHidden, toggle: toggleLegendHidden } =
        toggleLegendVisibleConfigOption;

    const cssClasses = classNames(
        'WidgetLegend',
        legendHidden && 'legend-hidden',
        allowLegendVisibilityEditing && 'legend-visibility-editing'
    );

    if (isLocked && legendHidden) {
        return <></>;
    }

    return (
        <div className={cssClasses}>
            {children}
            {allowLegendVisibilityEditing && (
                <WidgetLegendVisibilitySettings
                    alignDropdown={alignDropdown}
                    showSeries={showSeries}
                    legendHidden={legendHidden}
                    toggleLegendHidden={toggleLegendHidden}
                    toggleShowSeries={toggleShowSeries}
                />
            )}
        </div>
    );
}
